import api, { unwrap } from './client';
import type { PageResponse, SearchParams } from '@/types/api';

export interface ProcurementDashboardSummary {
  pendingApprovals: number;
  openPurchaseRequests: number;
  openPurchaseOrders: number;
  pendingGrns: number;
  draftRfqs: number;
}

export interface LineItemRequest {
  productId: number;
  quantity: number;
  unitPrice?: number;
  unitPriceEstimate?: number;
  taxPercentage?: number;
  batchNumber?: string;
  expiryDate?: string;
  notes?: string;
}

export interface PurchaseRequestResponse {
  id: number;
  uuid: string;
  requestNumber: string;
  requestStatus: string;
  storeId: number;
  totalAmount: number;
  approvalInstanceId?: number;
  items: { id: number; productId: number; quantity: number; unitPrice?: number; lineTotal?: number; receivedQuantity?: number }[];
}

export interface PurchaseOrderResponse {
  id: number;
  uuid: string;
  poNumber: string;
  poStatus: string;
  storeId: number;
  supplierId: number;
  totalAmount: number;
  receivedQuantity: number;
  items: { id: number; productId: number; quantity: number; unitPrice?: number; lineTotal?: number; receivedQuantity?: number }[];
}

export interface QuotationComparisonRow {
  quotationId: number;
  supplierId: number;
  supplierName: string;
  totalAmount: number;
  deliveryDate?: string;
  paymentTerms?: string;
  supplierRating?: number;
  selected: boolean;
  bestPrice: boolean;
  fastestDelivery: boolean;
}

export interface RfqSummary {
  id: number;
  uuid: string;
  rfqNumber: string;
  rfqStatus: string;
  purchaseRequestId?: number;
  deadlineAt?: string;
}

export interface QuotationSummary {
  id: number;
  uuid: string;
  quotationNumber: string;
  rfqId: number;
  supplierId: number;
  totalAmount: number;
  selected: boolean;
}

export interface VendorBillSummary {
  id: number;
  uuid: string;
  billNumber: string;
  billStatus: string;
  supplierId: number;
  totalAmount: number;
  paidAmount: number;
  dueDate?: string;
}

export interface VendorPaymentSummary {
  id: number;
  uuid: string;
  paymentNumber: string;
  paymentStatus: string;
  supplierId: number;
  vendorBillId?: number;
  amount: number;
  paymentDate: string;
}

export interface PendingApprovalResponse {
  instanceId: number;
  documentType: string;
  documentId: number;
  documentNumber?: string;
  totalAmount?: number;
  currentStageOrder: number;
  approvalStatus: string;
}

export interface ThreeWayMatchResult {
  vendorBillId: number;
  matchType: string;
  matchStatus: string;
  quantityMatched: boolean;
  priceMatched: boolean;
  taxMatched: boolean;
  inspectionMatched?: boolean;
  varianceAmount?: number;
}

export const procurementApi = {
  dashboardSummary: () =>
    unwrap<ProcurementDashboardSummary>(api.get('/v1/procurement/dashboard/summary')),

  listPendingApprovals: () =>
    unwrap<PendingApprovalResponse[]>(api.get('/v1/procurement/approvals/pending')),

  createPurchaseRequest: (body: {
    storeId: number;
    requiredDate?: string;
    notes?: string;
    items: LineItemRequest[];
  }) => unwrap<PurchaseRequestResponse>(api.post('/v1/procurement/purchase-requests', body)),

  submitPurchaseRequest: (id: number) =>
    unwrap<PurchaseRequestResponse>(api.post(`/v1/procurement/purchase-requests/${id}/submit`)),

  listPurchaseRequests: (params: SearchParams = {}) =>
    unwrap<PageResponse<PurchaseRequestResponse>>(api.get('/v1/procurement/purchase-requests', { params })),

  cancelPurchaseRequest: (id: number, reason?: string) =>
    unwrap<PurchaseRequestResponse>(
      api.post(`/v1/procurement/purchase-requests/${id}/cancel`, null, { params: { reason } }),
    ),

  cancelPurchaseOrder: (id: number, reason?: string) =>
    unwrap<PurchaseOrderResponse>(
      api.post(`/v1/procurement/purchase-orders/${id}/cancel`, null, { params: { reason } }),
    ),

  rejectGrnQc: (id: number, reason?: string) =>
    unwrap<{ id: number; grnNumber: string; grnStatus: string; posted: boolean }>(
      api.post(`/v1/procurement/goods-receipts/${id}/qc-reject`, null, { params: { reason } }),
    ),

  approvePurchaseRequest: (id: number, action: string, comments?: string) =>
    unwrap<PurchaseRequestResponse>(
      api.post(`/v1/procurement/purchase-requests/${id}/approve`, { action, comments }),
    ),

  createPurchaseOrder: (body: {
    storeId: number;
    supplierId: number;
    purchaseRequestId?: number;
    quotationId?: number;
    expectedDeliveryDate?: string;
    notes?: string;
    items?: LineItemRequest[];
  }) => unwrap<PurchaseOrderResponse>(api.post('/v1/procurement/purchase-orders', body)),

  submitPurchaseOrder: (id: number) =>
    unwrap<PurchaseOrderResponse>(api.post(`/v1/procurement/purchase-orders/${id}/submit`)),

  createRfq: (body: {
    purchaseRequestId?: number;
    deadlineAt?: string;
    notes?: string;
    supplierIds: number[];
    items: LineItemRequest[];
  }) => unwrap<RfqSummary>(api.post('/v1/procurement/rfqs', body)),

  createVendorBill: (body: {
    supplierId: number;
    purchaseOrderId?: number;
    goodsReceiptId?: number;
    supplierInvoiceNumber?: string;
    billDate?: string;
    dueDate?: string;
    items: LineItemRequest[];
  }) => unwrap<VendorBillSummary>(api.post('/v1/procurement/vendor-bills', body)),

  listPurchaseOrders: (params: SearchParams = {}) =>
    unwrap<PageResponse<PurchaseOrderResponse>>(api.get('/v1/procurement/purchase-orders', { params })),

  approvePurchaseOrder: (id: number, action: string, comments?: string) =>
    unwrap<PurchaseOrderResponse>(
      api.post(`/v1/procurement/purchase-orders/${id}/approve`, { action, comments }),
    ),

  createGoodsReceipt: (body: {
    purchaseOrderId: number;
    receiptDate?: string;
    warehouseCode?: string;
    notes?: string;
    items: {
      purchaseOrderItemId: number;
      productId: number;
      receivedQuantity: number;
      rejectedQuantity?: number;
      damagedQuantity?: number;
      batchNumber?: string;
      expiryDate?: string;
      binLocation?: string;
    }[];
  }) => unwrap<{ id: number; grnNumber: string; grnStatus: string; posted: boolean }>(
    api.post('/v1/procurement/goods-receipts', body),
  ),

  postGoodsReceipt: (id: number) =>
    unwrap<{ id: number; grnNumber: string; grnStatus: string; posted: boolean }>(
      api.post(`/v1/procurement/goods-receipts/${id}/post`),
    ),

  approveGrnQc: (id: number) =>
    unwrap<{ id: number; grnNumber: string; grnStatus: string; posted: boolean }>(
      api.post(`/v1/procurement/goods-receipts/${id}/qc-approve`),
    ),

  listGoodsReceipts: (params: SearchParams = {}) =>
    unwrap<PageResponse<{ id: number; grnNumber: string; grnStatus: string; posted: boolean; purchaseOrderId: number }>>(
      api.get('/v1/procurement/goods-receipts', { params }),
    ),

  listRfqs: (params: SearchParams = {}) =>
    unwrap<PageResponse<RfqSummary>>(api.get('/v1/procurement/rfqs', { params })),

  listQuotations: (params: SearchParams = {}) =>
    unwrap<PageResponse<QuotationSummary>>(api.get('/v1/procurement/quotations', { params })),

  compareQuotations: (rfqId: number) =>
    unwrap<QuotationComparisonRow[]>(api.get(`/v1/procurement/rfqs/${rfqId}/quotations/compare`)),

  recordQuotation: (body: {
    rfqId: number;
    supplierId: number;
    deliveryDate?: string;
    paymentTerms?: string;
    warrantyTerms?: string;
    items: LineItemRequest[];
  }) => unwrap<QuotationSummary>(api.post('/v1/procurement/quotations', body)),

  selectQuotation: (id: number) => unwrap<QuotationSummary>(api.post(`/v1/procurement/quotations/${id}/select`)),

  listVendorBills: (params: SearchParams = {}) =>
    unwrap<PageResponse<VendorBillSummary>>(api.get('/v1/procurement/vendor-bills', { params })),

  createVendorPayment: (body: {
    supplierId: number;
    vendorBillId: number;
    amount: number;
    paymentDate?: string;
    paymentMethod?: string;
    referenceNumber?: string;
    notes?: string;
  }) => unwrap<VendorPaymentSummary>(api.post('/v1/procurement/vendor-payments', body)),

  postVendorPayment: (id: number) =>
    unwrap<VendorPaymentSummary>(api.post(`/v1/procurement/vendor-payments/${id}/post`)),

  listVendorPayments: (params: SearchParams = {}) =>
    unwrap<PageResponse<VendorPaymentSummary>>(api.get('/v1/procurement/vendor-payments', { params })),

  analyticsDashboard: () =>
    unwrap<{
      openPurchaseRequests: number;
      openRfqs: number;
      openPurchaseOrders: number;
      pendingGrns: number;
      pendingApprovals: number;
      openVendorBills: number;
    }>(api.get('/v1/procurement/platform/analytics/dashboard')),

  threeWayMatch: (vendorBillId: number) =>
    unwrap<{ matchStatus: string; quantityMatched: boolean; priceMatched: boolean; taxMatched: boolean }>(
      api.post(`/v1/procurement/platform/vendor-bills/${vendorBillId}/match/three-way`),
    ),

  getMatch: (vendorBillId: number) =>
    unwrap<ThreeWayMatchResult | null>(api.get(`/v1/procurement/platform/vendor-bills/${vendorBillId}/match`)),

  listContracts: (params: SearchParams = {}) =>
    unwrap<PageResponse<{ id: number; contractNumber: string; title: string; contractStatus: string; endDate?: string }>>(
      api.get('/v1/shared/contracts', { params }),
    ),

  listSupplierScorecards: (supplierId: number) =>
    unwrap<
      {
        id: number;
        compositeScore: number;
        onTimeDeliveryScore?: number;
        qualityRating?: number;
      }[]
    >(api.get(`/v1/shared/vendors/${supplierId}/scorecards`)),

  listApprovalWorkflows: () =>
    unwrap<
      {
        id: number;
        code: string;
        name: string;
        documentType: string;
        minAmount?: number;
        maxAmount?: number;
        active: boolean;
      }[]
    >(api.get('/v1/procurement/approval-workflows')),

  saveApprovalWorkflow: (body: {
    code: string;
    name: string;
    documentType: string;
    minAmount?: number;
    maxAmount?: number;
    parallelStages?: boolean;
    stages: {
      stageOrder: number;
      name: string;
      approvalMode?: string;
      requiredApprovals?: number;
      assignments: { assigneeType: string; permissionCode?: string; roleCode?: string; userId?: number }[];
    }[];
  }) => unwrap<unknown>(api.post('/v1/procurement/approval-workflows', body)),

  reorderSuggestions: () =>
    unwrap<
      { storeId: number; productId: number; availableQty: number; reorderLevel: number; openRequestExists: boolean }[]
    >(api.get('/v1/procurement/platform/reorder/suggestions')),

  runReorder: () =>
    unwrap<PurchaseRequestResponse[]>(api.post('/v1/procurement/platform/reorder/run')),

  exportReport: (reportType: string, format: 'CSV' | 'PDF' | 'EXCEL' = 'CSV') =>
    api.get(`/v1/procurement/platform/reports/${reportType}/export`, {
      params: { format },
      responseType: 'blob',
    }),

  listCatalogs: (publishedOnly = false) =>
    unwrap<{ id: number; catalogName: string; catalogStatus: string; supplierId: number }[]>(
      api.get('/v1/procurement/platform/catalogs', { params: { publishedOnly } }),
    ),

  vendorPortalRequestLink: (organizationId: number, email: string) =>
    api.post('/v1/vendor-portal/auth/request-link', { organizationId, email }),
};
